import { cloneElement, FC, ReactElement, useState } from 'react';
import { HStack } from '@chakra-ui/react';

import { copyToClipBoard } from 'common/copyToClipboard';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Tooltip from 'components/_core/Tooltip';

import locales from './locales';

interface CopyableProps {
  children: ReactElement<{ as?: React.ElementType; onClick?: () => void }>;
  /**
   * Whether to show a popup on hover with a hint that the text is copyable
   */
  withHint?: boolean;
  /**
   * The text to copy to the clipboard
   */
  text: string;
}

/**
 *
 * A component that allows to copy text to the clipboard when clicking the children.
 *
 * It shows a tooltip with a susccess message when the text is copied.
 *
 * Use `withHint` to also show a popup on hover, with a hint that the text is copyable.
 *
 * @example
 * ```
 * <Copyable text="I'm in your clipboard" withHint>
 *  <p>Click to copy</p>
 * </Copyable>
 * ```
 */
export const Copyable: FC<CopyableProps> = ({
  children,
  text,
  withHint = false,
}) => {
  const initialTooltipContent = withHint ? locales.hint : null;
  const [tooltipContent, setTooltipContent] = useState<
    string | React.ReactNode | null
  >(initialTooltipContent);

  const handleCloseTooltip = () => {
    setTooltipContent(initialTooltipContent);
  };

  const handleCopy = () => {
    copyToClipBoard(text);
    setTooltipContent(
      <HStack alignItems="center" spacing="space-4">
        <Text color="white" fontSize="font-13" fontWeight="weight-400">
          {locales.copied}
        </Text>
        <SunshineIcon aria-label="tick" name="tick" size="icon-14" />
      </HStack>,
    );
  };

  return (
    <Tooltip
      closeOnClick={false}
      description={tooltipContent}
      isDisabled={!tooltipContent}
      isOpen={withHint ? undefined : Boolean(tooltipContent)}
      onClose={handleCloseTooltip}
    >
      {cloneElement(children, { as: 'button', onClick: handleCopy })}
    </Tooltip>
  );
};
